import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { Divider, Flex, Label, Section, Table } from "@samskip/frontend-components";
import {
  getPartnerDefaultAddresses,
  getPartnerRepresentatives,
  getPartnerCustomsInfo,
} from "@state/slices/partnerSlice";
import { useTranslation } from "react-i18next";

const formatDate = (dateString: string) => {
  if (!dateString || dateString.includes("0001")) return "";
  return new Date(dateString).toLocaleDateString("en-GB");
};

const CompanyEmployeeView: React.FC<{ company: any }> = ({ company }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const defaultAddresses = useAppSelector((state) => state.partnerSlice.defaultAddresses);
  const representatives = useAppSelector((state) => state.partnerSlice.representatives);
  const customsInfo = useAppSelector((state) => state.partnerSlice.customsInfo);

  useEffect(() => {
    if (company?.PartnerCode) {
      dispatch(getPartnerDefaultAddresses({ partnerCode: company.PartnerCode }));
      dispatch(getPartnerRepresentatives(company.PartnerCode));
      dispatch(getPartnerCustomsInfo(company.CompanyRegNo));
    }
  }, [company?.PartnerCode, dispatch]);

  const companyRow1Headers = [
    { id: "partnerCode", name: t("LABEL_PARTNER"), className: "w-1/4" },
    { id: "fullName", name: t("LABEL_NAME"), className: "w-1/4" },
    { id: "compRegNo", name: t("LABEL_SSN"), className: "w-1/4" },
    { id: "sapId", name: t("LABEL_SAP_ID"), className: "w-1/4" },
  ];

  const companyRow1Cells = [
    company?.PartnerCode,
    company?.FullName,
    company?.CompanyRegNo || "",
    company?.SapID || "",
  ];

  const companyRow2Headers = [
    { id: "address", name: t("LABEL_ADDRESS"), className: "w-1/3" },
    { id: "city", name: t("LABEL_CITY"), className: "w-1/3" },
    { id: "country", name: t("LABEL_COUNTRY"), className: "w-1/3" },
  ];

  const companyRow2Cells = [company?.Address2, company?.Address3, company?.Country];

  const addressHeaders = [
    { id: "addressType", name: t("LABEL_ADDRESS_TYPE"), className: "w-1/6" },
    { id: "name", name: t("LABEL_NAME"), className: "w-1/6" },
    { id: "address", name: t("LABEL_ADDRESS"), className: "w-1/6" },
    { id: "zip", name: t("LABEL_POSTALCODE"), className: "w-1/6" },
    { id: "location", name: t("LABEL_LOCATION"), className: "w-1/6" },
    { id: "country", name: t("LABEL_COUNTRY"), className: "w-1/6" },
  ];

  const contactHeaders = [
    { id: "transport", name: t("LABEL_IMP_EXP"), className: "w-1/3" },
    { id: "department", name: t("LABEL_DEPARTMENT"), className: "w-1/3" },
    { id: "employee", name: t("LABEL_EMPLOYEE"), className: "w-1/3" },
  ];

  return (
    <Flex gap="large" vertical>
      <Section title={t("LABEL_COMPANY")}>
        <Flex gap="medium" vertical>
          <Table id="company-info-1">
            <Table.Header>
              <Table.HeaderRow>
                {companyRow1Headers.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              <Table.Row id="row1">
                {companyRow1Cells.map((cell, i) => (
                  <Table.Cell
                    className={companyRow1Headers[i].className}
                    id={companyRow1Headers[i].id}
                    key={companyRow1Headers[i].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>

          <Table id="company-info-2">
            <Table.Header>
              <Table.HeaderRow>
                {companyRow2Headers.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              <Table.Row id="row2">
                {companyRow2Cells.map((cell, i) => (
                  <Table.Cell
                    className={companyRow2Headers[i].className}
                    id={companyRow2Headers[i].id}
                    key={companyRow2Headers[i].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>
        </Flex>
      </Section>

      <Divider />

      <Section title={t("LABEL_COMPANY_ADDRESSES")}>
        <Table id="company-addresses">
          <Table.Header>
            <Table.HeaderRow>
              {addressHeaders.map((h) => (
                <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                  {h.name}
                </Table.HeaderCell>
              ))}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {defaultAddresses.map((addr) => (
              <Table.Row id={addr.AddressCode} key={addr.AddressCode}>
                {[
                  addr.AddressTypeDescription,
                  addr.FullName,
                  addr.Address1,
                  addr.PostCode,
                  addr.City,
                  addr.CountryName,
                ].map((cell, index) => (
                  <Table.Cell
                    className={addressHeaders[index].className}
                    ellipsis="normal"
                    id={addressHeaders[index].id}
                    key={addressHeaders[index].id}
                  >
                    {cell || ""}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Section>

      <Divider />

      <Section title={t("LABEL_REPRESENTATIVES")}>
        {representatives.length > 0 ? (
          <Table id="company-contacts">
            <Table.Header>
              <Table.HeaderRow>
                {contactHeaders.map((h) => (
                  <Table.HeaderCell className={h.className} id={h.id} key={h.id}>
                    {h.name}
                  </Table.HeaderCell>
                ))}
              </Table.HeaderRow>
            </Table.Header>
            <Table.Body>
              {representatives.map((rep, index) => (
                <Table.Row id={String(rep.CodeValue || index)} key={rep.CodeValue || index}>
                  {[rep.TransportEng, rep.DepartmentEng, rep.EmployeeName].map((cell, i) => (
                    <Table.Cell
                      className={contactHeaders[i].className}
                      ellipsis="normal"
                      id={contactHeaders[i].id}
                      key={contactHeaders[i].id}
                    >
                      {cell || ""}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : null}
      </Section>

      <Divider />

      <Section title={t("LABEL_CUSTOMS_STATUS")}>
        {customsInfo ? (
          <Table id="customs-info">
            <Table.Body>
              <Table.Row id="flight">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_FLIGHT")}</Label>
                </Table.Cell>
                <Table.Cell id="value">
                  {customsInfo.Flight ? t("LABEL_YES") : t("LABEL_NO")}
                </Table.Cell>
              </Table.Row>
              <Table.Row id="sea">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_SEA")}</Label>
                </Table.Cell>
                <Table.Cell id="value">
                  {customsInfo.Sea ? t("LABEL_YES") : t("LABEL_NO")}
                </Table.Cell>
              </Table.Row>
              <Table.Row id="customType">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_CUSTOM_TYPE_AMOUNT")}</Label>
                </Table.Cell>
                <Table.Cell id="value">{customsInfo.CustomTypeAmount || ""}</Table.Cell>
              </Table.Row>
              <Table.Row id="additionalLines">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_ADDITIONAL_LINES")}</Label>
                </Table.Cell>
                <Table.Cell id="value">{customsInfo.AdditionalLineAmount || ""}</Table.Cell>
              </Table.Row>
              <Table.Row id="dealership">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_DEALERSHIP")}</Label>
                </Table.Cell>
                <Table.Cell id="value">
                  {customsInfo.Dealership ? t("LABEL_YES") : t("LABEL_NO")}
                </Table.Cell>
              </Table.Row>
              <Table.Row id="customCreditRated">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_CUSTOM_CREDIT_RATED")}</Label>
                </Table.Cell>
                <Table.Cell id="value">
                  {customsInfo.CustomCreditRated ? t("LABEL_YES") : t("LABEL_NO")}
                </Table.Cell>
              </Table.Row>
              <Table.Row id="dealershipDate">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_DEALERSHIP_DATE")}</Label>
                </Table.Cell>
                <Table.Cell id="value">{formatDate(customsInfo.DealershipDate)}</Table.Cell>
              </Table.Row>
              <Table.Row id="employee">
                <Table.Cell className="w-1/3" id="label">
                  <Label fontWeight="semibold">{t("LABEL_CUSTOMS_OFFICAL")}</Label>
                </Table.Cell>
                <Table.Cell id="value">{customsInfo.Employee || ""}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        ) : null}
      </Section>
    </Flex>
  );
};

export default CompanyEmployeeView;